import {
  type FacilityOption,
  type HealthCheckupFacilityContract,
  type MedicalCheckupOption,
  type MedicalCheckupPlan,
  calculateOptionCounterPayAmount,
  calculateOptionSelfPayAmount,
} from "@/server/models/medical-checkup/medical-checkups";
import { OptionRow } from "./OptionRow";
import type { OptionPriceInfo, OptionSubsidy, PlanSubsidy } from "./types";

// オプション検査行コンポーネント（計算ロジック込み）
export const OptionRowWithCalculation = ({
  option,
  planSubsidies,
  optionSubsidies,
  contract,
  fiscalYear,
}: {
  option: FacilityOption;
  planSubsidies: Partial<Record<MedicalCheckupPlan, PlanSubsidy>>;
  optionSubsidies?: Partial<Record<MedicalCheckupOption, OptionSubsidy>>;
  contract: HealthCheckupFacilityContract;
  fiscalYear: number;
}) => {
  const subsidyAmount = optionSubsidies?.[option.code]?.max_amount_of_subsidy;
  const hasSubsidy = typeof subsidyAmount === "number" && subsidyAmount > 0;

  // コースごとの金額がある場合
  if (option.prices && option.prices.length > 0) {
    const subsidizedPlanCodes = Object.keys(
      planSubsidies,
    ) as MedicalCheckupPlan[];

    const planPrices = option.prices
      .filter((p) => subsidizedPlanCodes.includes(p.plan_code))
      .map((p) => {
        const selfPayAmount =
          subsidyAmount != null
            ? calculateOptionSelfPayAmount({
                subsidyAmount,
                priceRange: { min: p.price, max: p.price },
              }).min
            : p.price;
        const counterPayAmount = calculateOptionCounterPayAmount({
          contract,
          selfPayAmount,
          basePrice: p.price,
        });
        return {
          planCode: p.plan_code,
          planLabel: p.plan_label,
          selfPayAmount,
          counterPayAmount,
        };
      });

    const priceInfo: OptionPriceInfo =
      planPrices.length > 0 ? { kind: "perPlan", planPrices } : null;

    return (
      <OptionRow
        label={option.label}
        description={option.description}
        priceInfo={priceInfo}
        hasSubsidy={hasSubsidy}
        fiscalYear={fiscalYear}
      />
    );
  }

  // 単一価格の場合
  let priceInfo: OptionPriceInfo = null;
  if (option.price !== null) {
    const selfPayAmount =
      subsidyAmount != null
        ? calculateOptionSelfPayAmount({
            subsidyAmount,
            priceRange: { min: option.price, max: option.price },
          }).min
        : option.price;
    const counterPayAmount = calculateOptionCounterPayAmount({
      contract,
      selfPayAmount,
      basePrice: option.price,
    });
    priceInfo = { kind: "single", selfPayAmount, counterPayAmount };
  }

  return (
    <OptionRow
      label={option.label}
      description={option.description}
      priceInfo={priceInfo}
      hasSubsidy={hasSubsidy}
      fiscalYear={fiscalYear}
    />
  );
};
